import { useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { PageNavigation } from "../components/ui/page-navigation";
import { Link } from "wouter";
import HasPermission from "../components/auth/HasPermission";
import { User, Plug2, CreditCard, ShieldCheck } from "lucide-react";

const SETTINGS_SECTIONS = {
  account: {
    title: "Account",
    description: "Profile details and personal account controls.",
    items: [
      {
        title: "Profile & Settings",
        description: "Manage your personal information, clinic access, and compliance details.",
        href: "/settings/profile",
        action: "Open profile settings",
        icon: User,
      },
    ],
  },
  integrations: {
    title: "Integrations",
    description: "Connected systems used across clinical workflows.",
    items: [
      {
        title: "EHR Integration",
        description: "Manage Athena and EHR connectivity for clinical workflows.",
        href: "/settings/ehr-integration",
        action: "Open integration settings",
        icon: Plug2,
      },
    ],
  },
  billing: {
    title: "Billing",
    description: "Payment methods and billing preferences.",
    items: [
      {
        title: "Payment & Billing",
        description: "Review payment methods and billing preferences.",
        href: "/settings/payment-billing",
        action: "Open billing settings",
        icon: CreditCard,
      },
    ],
  },
  administration: {
    title: "Administration",
    description: "Role management and permission controls.",
    items: [
      {
        title: "Admin Settings",
        description: "Review role defaults and apply explicit permission overrides to users.",
        href: "/admin/rbac",
        action: "Open admin settings",
        icon: ShieldCheck,
      },
    ],
  },
};

function SettingsCard({ title, description, href, action, icon: Icon }) {
  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Icon className="h-5 w-5" />
            </div>
          )}
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">{description}</p>
        <Link href={href} className="text-sm font-medium text-blue-600 hover:underline">
          {action}
        </Link>
      </CardContent>
    </Card>
  );
}

function SettingsSection({ title, description, items }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 border-b border-gray-100 pb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <SettingsCard key={item.href} {...item} />
        ))}
      </div>
    </section>
  );
}

function Settings() {
  useEffect(() => {
    document.title = "Settings - Seismic Connect";
  }, []);

  return (
    <div className="space-y-6">
      <PageNavigation
        title="Settings"
        subtitle="Manage account, billing, integrations, and admin configuration."
      />
      <div className="space-y-6">
        <SettingsSection {...SETTINGS_SECTIONS.account} />
        <HasPermission required="settings.ehr_integration" level="read">
          <SettingsSection {...SETTINGS_SECTIONS.integrations} />
        </HasPermission>
        <HasPermission required="settings.payment_billing" level="read">
          <SettingsSection {...SETTINGS_SECTIONS.billing} />
        </HasPermission>
        <HasPermission required="admin.manage_rbac" level="read">
          <SettingsSection {...SETTINGS_SECTIONS.administration} />
        </HasPermission>
      </div>
    </div>
  );
}

export default Settings;
